import {
  AlertCircle, CheckCircle,
  Download,
  FileText,
  Filter,
  Loader,
  Play,
  Plus,
  RefreshCw, Trash2,
  X,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import WorkerSelector from '../components/WorkerSelector';
import invoiceService from '../services/invoiceService';
import CreditNoteManager from './CreditNoteManager';
import './CreditNotes.css';

/* ── Status configs ─────────────────────────────────────────────────── */
const CN_STATUS_CFG = {
  DRAFT: { label: 'Bozza', cls: 'cn-draft' },
  SENT_TO_ACUBE: { label: 'Inviata ad A-Cube', cls: 'cn-sent' },
  COMPLETED: { label: 'Completata', cls: 'cn-completed' },
  ERROR: { label: 'Errore', cls: 'cn-error' },
};

const ACUBE_STATUS = {
  sent: { label: 'Inviata', cls: 'acube-sent' },
  delivered: { label: 'Consegnata', cls: 'acube-delivered' },
  quarantena: { label: 'In quarantena', cls: 'acube-quarantena' },
  error: { label: 'Errore', cls: 'acube-error' },
  pending: { label: 'In attesa', cls: 'acube-pending' },
};

const REASON_LABELS = {
  CHARGEBACK: 'Chargeback',
  STRIPE_REVERSAL: 'Storno Stripe',
  DATA_ERROR: 'Errore Dati',
  DUPLICATE: 'Duplicato',
  CANCELLATION: 'Cancellazione',
  ADMIN_CORRECTION: 'Correzione Admin',
  CUSTOMER_REQUEST: 'Richiesta Cliente',
  OTHER: 'Altro',
};

/* ── Sub-components ─────────────────────────────────────────────────── */
function CnStatusBadge({ status }) {
  const cfg = CN_STATUS_CFG[status] || { label: status || '—', cls: 'cn-draft' };
  return <span className={`cn-status-badge ${cfg.cls}`}>{cfg.label}</span>;
}

function AcubeBadge({ status }) {
  if (!status) return null;
  const cfg = ACUBE_STATUS[status] || { label: status, cls: 'acube-pending' };
  return <span className={`acube-badge ${cfg.cls}`}>{cfg.label}</span>;
}

/* ── Helpers ────────────────────────────────────────────────────────── */
function formatCurrency(cents) {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(
    (cents ?? 0) / 100
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('it-IT', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

/* ── Main component ─────────────────────────────────────────────────── */
const CreditNotes = () => {
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // create panel state
  const [showCreate, setShowCreate] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [createWorker, setCreateWorker] = useState('');

  // filters
  const [workerSearch, setWorkerSearch] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // per-row action state
  const [actionBusy, setActionBusy] = useState(null);
  const [actionFeedback, setActionFeedback] = useState({});

  useEffect(() => {
    loadCreditNotes();
    loadWorkers();
  }, []);

  async function loadWorkers() {
    try {
      const res = await invoiceService.getWorkerPaymentProfiles();
      if (res && res.profiles) {
        setWorkers(res.profiles);
      }
    } catch {
      // workers list is optional; silently ignore
    }
  }

  async function loadCreditNotes() {
    try {
      setLoading(true);
      setError(null);
      const response = await invoiceService.getCreditNotes();
      setCreditNotes(response.data || []);
    } catch (err) {
      setError(err.message || 'Errore nel caricamento delle note di credito');
    } finally {
      setLoading(false);
    }
  }

  /* ── feedback helpers ─────────────────────────────────────────── */
  function showFeedback(id, type) {
    setActionFeedback((prev) => ({ ...prev, [id]: type }));
    setTimeout(
      () => setActionFeedback((prev) => { const n = { ...prev }; delete n[id]; return n; }),
      2500
    );
  }

  /* ── actions ──────────────────────────────────────────────────── */
  async function handleSend(cnId, cnNumber) {
    if (!window.confirm(
      `Vuoi inviare la nota di credito ${cnNumber} ad A-Cube?\n\nLa nota verrà processata e non potrà più essere modificata.`
    )) return;
    try {
      setActionBusy(cnId);
      await invoiceService.sendCreditNoteToAcube(cnId);
      showFeedback(cnId, 'ok');
      await loadCreditNotes();
    } catch {
      showFeedback(cnId, 'error');
    } finally {
      setActionBusy(null);
    }
  }

  async function handleDelete(cnId, cnNumber) {
    if (!window.confirm(
      `Sei sicuro di voler eliminare la nota di credito ${cnNumber}?\n\nI transfer associati torneranno disponibili.`
    )) return;
    try {
      setActionBusy(cnId);
      await invoiceService.deleteCreditNote(cnId);
      showFeedback(cnId, 'ok');
      setCreditNotes((prev) => prev.filter((cn) => cn.id !== cnId));
    } catch {
      showFeedback(cnId, 'error');
    } finally {
      setActionBusy(null);
    }
  }

  async function handleDownloadPdf(cn) {
    try {
      setActionBusy(cn.id);
      const blob = await invoiceService.downloadCreditNotePdf(cn.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nota-credito-${cn.creditNoteNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showFeedback(cn.id, 'ok');
    } catch {
      showFeedback(cn.id, 'error');
    } finally {
      setActionBusy(null);
    }
  }

  /* ── derived counts ───────────────────────────────────────────── */
  const counts = {
    total: creditNotes.length,
    DRAFT: creditNotes.filter((c) => c.status === 'DRAFT').length,
    SENT_TO_ACUBE: creditNotes.filter((c) => c.status === 'SENT_TO_ACUBE').length,
    COMPLETED: creditNotes.filter((c) => c.status === 'COMPLETED').length,
    ERROR: creditNotes.filter((c) => c.status === 'ERROR').length,
  };

  const filtered = creditNotes.filter((cn) => {
    if (statusFilter !== 'all' && cn.status !== statusFilter) return false;
    if (reasonFilter && cn.reason !== reasonFilter) return false;
    if (workerSearch) {
      const q = workerSearch.toLowerCase();
      if (!cn.workerName?.toLowerCase().includes(q) && !cn.workerEmail?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const hasActiveFilters = statusFilter !== 'all' || reasonFilter || workerSearch;
  /* ── render ───────────────────────────────────────────────────── */
  return (
    <div className="cn-page">

      {/* ── Header ── */}
      <div className="cn-header">
        <div className="cn-header-left">
          <h1><FileText size={26} /> Note di Credito</h1>
          <span className="cn-header-sub">Storno fatture TD04 — gestione A-Cube</span>
        </div>
        <div className="cn-header-actions">
          <button
            className="cn-ctrl-btn cn-ctrl-btn-primary"
            onClick={() => { setShowCreate((v) => !v); setCreateWorker(''); }}
          >
            {showCreate ? <X size={14} /> : <Plus size={14} />}
            {showCreate ? 'Annulla' : 'Nuova nota di credito'}
          </button>
          <button className="cn-ctrl-btn" onClick={loadCreditNotes} disabled={loading} title="Aggiorna">
            <RefreshCw size={14} className={loading ? 'spinning' : ''} />
            Aggiorna
          </button>
        </div>
      </div>

      {/* ── Create panel ── */}
      {showCreate && (
        <div className="cn-create-panel">
          <div className="cn-create-panel-header">
            <FileText size={16} />
            <span>Nuova nota di credito</span>
          </div>
          <div className="cn-create-panel-body">
            <WorkerSelector
              workers={workers}
              value={createWorker}
              onChange={setCreateWorker}
              label="Percettore"
              placeholder="Cerca percettore per nome, email o locale…"
              required={true}
            />
            {createWorker && (
              <CreditNoteManager
                workerPaymentProfileId={createWorker}
                onClose={() => {
                  setShowCreate(false);
                  setCreateWorker('');
                  loadCreditNotes();
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* ── KPI cards ── */}
      <div className="cn-kpi-row">
        {[
          { label: 'Totale', value: counts.total, cls: '' },
          { label: 'Bozze', value: counts.DRAFT, cls: 'kpi-draft' },
          { label: 'Inviate', value: counts.SENT_TO_ACUBE, cls: 'kpi-sent' },
          { label: 'Completate', value: counts.COMPLETED, cls: 'kpi-completed' },
          { label: 'Errori', value: counts.ERROR, cls: 'kpi-error' },
        ].map((k) => (
          <div key={k.label} className={`cn-kpi-card ${k.cls}`}>
            <span className="cn-kpi-value">{k.value}</span>
            <span className="cn-kpi-label">{k.label}</span>
          </div>
        ))}
      </div>

      {/* ── Error banner ── */}
      {error && (
        <div className="cn-alert cn-alert-error">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {/* ── Filters ── */}
      <div className="cn-filters-card">
        <div className="cn-filters-row">
          <div className="cn-filter-group">
            <Filter size={14} />
            <input
              type="text"
              placeholder="Cerca percettore (nome o email)…"
              value={workerSearch}
              onChange={(e) => setWorkerSearch(e.target.value)}
            />
          </div>
          <div className="cn-filter-group">
            <select value={reasonFilter} onChange={(e) => setReasonFilter(e.target.value)}>
              <option value="">Tutti i motivi</option>
              {Object.entries(REASON_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>
          {hasActiveFilters && (
            <button
              className="cn-clear-btn"
              onClick={() => { setWorkerSearch(''); setReasonFilter(''); setStatusFilter('all'); }}
            >
              <XCircle size={13} /> Reset filtri
            </button>
          )}
        </div>

        {/* Status pills */}
        <div className="cn-status-pills">
          {[
            { key: 'all', label: `Tutte (${counts.total})` },
            { key: 'DRAFT', label: `Bozze (${counts.DRAFT})` },
            { key: 'SENT_TO_ACUBE', label: `Inviate (${counts.SENT_TO_ACUBE})` },
            { key: 'COMPLETED', label: `Completate (${counts.COMPLETED})` },
            { key: 'ERROR', label: `Errori (${counts.ERROR})` },
          ].map((p) => (
            <button
              key={p.key}
              className={`cn-pill ${statusFilter === p.key ? 'cn-pill-active' : ''}`}
              onClick={() => setStatusFilter(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div className="cn-empty">
          <Loader size={22} className="spinning" />
          <span>Caricamento note di credito…</span>
        </div>
      )}

      {/* ── Empty ── */}
      {!loading && filtered.length === 0 && (
        <div className="cn-empty">
          <FileText size={40} />
          <p>
            {creditNotes.length === 0
              ? 'Nessuna nota di credito presente.'
              : 'Nessun risultato per i filtri selezionati.'}
          </p>
        </div>
      )}

      {/* ── Table ── */}
      {!loading && filtered.length > 0 && (
        <div className="cn-table-wrapper">
          <table className="cn-table">
            <thead>
              <tr>
                <th>Nota di Credito</th>
                <th>Percettore</th>
                <th className="hide-mobile">Fattura</th>
                <th className="hide-mobile">Motivo</th>
                <th className="text-right">Importo</th>
                <th>Stato</th>
                <th className="hide-mobile">A-Cube</th>
                <th className="hide-mobile">Data</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((cn) => {
                const isBusy = actionBusy === cn.id;
                const fb = actionFeedback[cn.id];
                return (
                  <tr key={cn.id} className={cn.status === 'ERROR' ? 'row-error' : ''}>
                    <td>
                      <span className="cn-number-pill">{cn.creditNoteNumber}</span>
                    </td>
                    <td>
                      <div className="cn-worker-name">{cn.workerName || '—'}</div>
                      <div className="cn-worker-email">{cn.workerEmail || ''}</div>
                    </td>
                    <td className="hide-mobile">
                      <span className="cn-invoice-number">{cn.invoice?.invoiceNumber || '—'}</span>
                      {cn.invoice?.periodStart && (
                        <div className="cn-date-small">
                          {new Date(cn.invoice.periodStart).toLocaleDateString('it-IT')}
                        </div>
                      )}
                    </td>
                    <td className="hide-mobile">
                      {cn.reason
                        ? <span className="cn-reason-badge">{REASON_LABELS[cn.reason] || cn.reason}</span>
                        : '—'}
                    </td>
                    <td className="text-right">
                      <strong>{formatCurrency(cn.totalRefund ?? cn.totalAmount)}</strong>
                    </td>
                    <td>
                      <CnStatusBadge status={cn.status} />
                    </td>
                    <td className="hide-mobile">
                      <AcubeBadge status={cn.acubeCreditNoteStatus} />
                      {cn.acubeCreditNoteError && (
                        <div className="cn-error-msg" title={cn.acubeCreditNoteError}>
                          {cn.acubeCreditNoteError.substring(0, 55)}…
                        </div>
                      )}
                    </td>
                    <td className="hide-mobile">
                      <span className="cn-date-cell">{formatDate(cn.generatedAt)}</span>
                    </td>
                    <td className="cn-actions-cell">
                      {fb === 'ok' && <CheckCircle size={16} className="cn-fb-ok" />}
                      {fb === 'error' && <XCircle size={16} className="cn-fb-error" />}
                      {!fb && (
                        <>
                          {cn.status === 'DRAFT' && (
                            <button
                              className="cn-btn cn-btn-primary"
                              disabled={isBusy}
                              onClick={() => handleSend(cn.id, cn.creditNoteNumber)}
                              title="Invia ad A-Cube"
                            >
                              {isBusy ? <Loader size={11} className="spinning" /> : <Play size={11} />}
                              Invia
                            </button>
                          )}
                          {cn.acubeCreditNoteId && (
                            <button
                              className="cn-btn cn-btn-ghost"
                              disabled={isBusy}
                              onClick={() => handleDownloadPdf(cn)}
                              title="Scarica PDF"
                            >
                              <Download size={11} /> PDF
                            </button>
                          )}
                          {cn.status === 'DRAFT' && (
                            <button
                              className="cn-btn cn-btn-danger"
                              disabled={isBusy}
                              onClick={() => handleDelete(cn.id, cn.creditNoteNumber)}
                              title="Elimina bozza"
                            >
                              <Trash2 size={11} />
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CreditNotes;
